import type { IncidentsRecomputeChunk } from "@visioni/a3s-monitoring/app";

export interface RecomputeProgress {
  readonly summary: string;
  readonly failed: boolean;
  /** A line per day that failed or is being retried, in time order. */
  readonly lines: readonly string[];
}

/**
 * Where a recompute has got: waiting for its first day, running, done with
 * what it counted, or how many days failed. Days that failed or are being
 * retried get their own line with the error, since those are what someone
 * would act on.
 */
export function recomputeProgress(
  run: { readonly chunks: readonly IncidentsRecomputeChunk[] },
  formatMoment: (iso: string) => string,
): RecomputeProgress {
  const total = run.chunks.length;
  const done = run.chunks.filter((chunk) => chunk.status === "done");
  const failed = run.chunks.filter((chunk) => chunk.status === "failed");
  const days = (count: number) => `${String(count)} of ${String(total)} days`;
  const span = (chunk: IncidentsRecomputeChunk) => `${formatMoment(chunk.since)} to ${formatMoment(chunk.until)}`;

  const lines = run.chunks.flatMap((chunk) => {
    const why = chunk.lastError === undefined ? "" : `: ${chunk.lastError}`;
    if (chunk.status === "failed") return [`${span(chunk)} failed after ${String(chunk.attempts)} attempts${why}`];
    if (chunk.status !== "done" && chunk.attempts > 0 && chunk.lastError !== undefined) {
      const attempt = chunk.status === "running" ? chunk.attempts : chunk.attempts + 1;
      return [`${span(chunk)} retrying (attempt ${String(attempt)})${why}`];
    }
    return [];
  });

  const finished = done.length + failed.length === total;
  let summary: string;
  if (failed.length > 0 && finished) summary = `${days(failed.length)} failed`;
  else if (finished) {
    const sum = (pick: (chunk: IncidentsRecomputeChunk) => number | undefined) =>
      done.reduce((total, chunk) => total + (pick(chunk) ?? 0), 0);
    summary = `Done · ${count(
      sum((c) => c.genuineIncidents),
      "genuine incident",
    )}, ${count(
      sum((c) => c.nonGenuineActivations),
      "non-genuine activation",
    )}`;
  } else {
    const started = run.chunks.some((chunk) => chunk.attempts > 0);
    summary = `${started ? "Running" : "Waiting"} · ${days(done.length)} done`;
  }
  return { summary, failed: failed.length > 0, lines };
}

const count = (n: number, noun: string) => `${n.toLocaleString()} ${noun}${n === 1 ? "" : "s"}`;
